import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Seo } from '@/components/seo/Seo';
import { CarSearchFilters, type CarFilters } from '@/components/cars/CarSearchFilters';
import { CarCard } from '@/components/cars/CarCard';
import { ModelCard } from '@/components/cars/ModelCard';
import { ViewModeToggle, type CarViewMode } from '@/components/cars/ViewModeToggle';
import { groupCarsByModel } from '@/lib/cars/groupByModel';
import { useCarSearch, type SortOption } from '@/hooks/useCarSearch';
import { breadcrumbJsonLd, HOME_CRUMB } from '@/lib/seo/breadcrumbs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { CarCardGridSkeleton } from '@/components/cars/CarCardSkeleton';
import { CarSearchAutocomplete } from '@/components/search/CarSearchAutocomplete';
import { ShareButton } from '@/components/share/ShareButton';
import { SaveSearchButton } from '@/components/cars/SaveSearchButton';
import { SlidersHorizontal, Car, ArrowUpDown, AlertTriangle, RefreshCw } from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';

const ITEMS_PER_PAGE = 24;

const defaultFilters: CarFilters = {
  query: '',
  maxPrice: null,
  powertrain: [],
  bodyType: [],
  drivetrain: null,
  minRange: null,
  minTrunk: null,
  minTowing: null,
  minSeats: null,
  includeOptionalSeats: true,
  minPower: null,
  minChargingDcKw: null,
  maxAcceleration: null,
  brandId: null,
  modelId: null,
  showUpcoming: null,
  minIsofix: null,
  threeIsofixSecondRow: 'any',
};

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'updated_desc', label: 'Senest opdateret' },
  { value: 'price_asc', label: 'Pris: lav til høj' },
  { value: 'price_desc', label: 'Pris: høj til lav' },
  { value: 'range_desc', label: 'Rækkevidde: høj til lav' },
  { value: 'power_desc', label: 'Effekt: høj til lav' },
  { value: 'year_desc', label: 'Årgang: nyeste først' },
  { value: 'year_asc', label: 'Årgang: ældste først' },
  { value: 'votes_desc', label: 'Mest anbefalede' },
  { value: 'recommend_pct_desc', label: 'Højeste anbefalingsprocent' },
  { value: 'charging_dc_desc', label: 'DC-ladning: hurtigst først' },
  { value: 'trunk_desc', label: 'Bagagerum: størst først' },
  { value: 'isofix_desc', label: 'Isofix: flest pladser først' },
];

export default function Cars() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<CarFilters>(() => {
    // Initialize from URL params
    const showUpcomingParam = searchParams.get('showUpcoming');
    return {
      query: searchParams.get('q') || '',
      maxPrice: searchParams.get('maxPrice') ? parseInt(searchParams.get('maxPrice')!) : null,
      powertrain: searchParams.get('powertrain')?.split(',').filter(Boolean) || [],
      bodyType: searchParams.get('bodyType')?.split(',').filter(Boolean) || [],
      drivetrain: searchParams.get('drivetrain') || null,
      minRange: searchParams.get('minRange') ? parseInt(searchParams.get('minRange')!) : null,
      minTrunk: searchParams.get('minTrunk') ? parseInt(searchParams.get('minTrunk')!) : null,
      minTowing: searchParams.get('minTowing') ? parseInt(searchParams.get('minTowing')!) : null,
      minSeats: searchParams.get('minSeats') ? parseInt(searchParams.get('minSeats')!) : null,
      includeOptionalSeats: searchParams.get('optSeats') !== '0',
      minPower: searchParams.get('minPower') ? parseInt(searchParams.get('minPower')!) : null,
      minChargingDcKw: searchParams.get('minChargingDcKw') ? parseInt(searchParams.get('minChargingDcKw')!) : null,
      maxAcceleration: searchParams.get('maxAcceleration') ? parseFloat(searchParams.get('maxAcceleration')!) : null,
      brandId: searchParams.get('brandId') || null,
      modelId: searchParams.get('modelId') || null,
      showUpcoming: showUpcomingParam as 'all' | 'only' | 'exclude' | null,
      minIsofix: searchParams.get('minIsofix') ? parseInt(searchParams.get('minIsofix')!) : null,
      threeIsofixSecondRow: (searchParams.get('iso3row') as 'any' | 'yes' | 'no') || 'any',
    };
  });
  const [sort, setSort] = useState<SortOption>((searchParams.get('sort') as SortOption) || 'updated_desc');
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') || '1');
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const compareMode = searchParams.get('compare') === '1';
  const [viewMode, setViewMode] = useState<CarViewMode>(() => {
    if (compareMode) return 'variant';
    if (typeof window === 'undefined') return 'model';
    const saved = window.localStorage.getItem('cars:viewMode');
    return saved === 'variant' ? 'variant' : 'model';
  });
  useEffect(() => {
    if (compareMode) {
      if (viewMode !== 'variant') setViewMode('variant');
      return;
    }
    try { window.localStorage.setItem('cars:viewMode', viewMode); } catch { /* ignore */ }
  }, [viewMode, compareMode]);
  const resultsRef = React.useRef<HTMLDivElement>(null);

  // Fetch brands for filter dropdown
  const { data: brands = [] } = useQuery({
    queryKey: ['brands-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch models — alle aktive modeller; filtreres efter mærke når valgt
  const { data: models = [] } = useQuery({
    queryKey: ['models-for-filter', filters.brandId],
    queryFn: async () => {
      let query = supabase
        .from('models')
        .select('id, name, brand_id, brands(name)')
        .eq('is_active', true)
        .order('name');
      if (filters.brandId) query = query.eq('brand_id', filters.brandId);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });


  // Fetch cars with filters
  const { data: cars = [], isLoading, isError, error, refetch } = useCarSearch({ filters, sort });

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.query) params.set('q', filters.query);
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice.toString());
    if (filters.powertrain.length) params.set('powertrain', filters.powertrain.join(','));
    if (filters.bodyType.length) params.set('bodyType', filters.bodyType.join(','));
    if (filters.drivetrain) params.set('drivetrain', filters.drivetrain);
    if (filters.minRange) params.set('minRange', filters.minRange.toString());
    if (filters.minTrunk) params.set('minTrunk', filters.minTrunk.toString());
    if (filters.minTowing) params.set('minTowing', filters.minTowing.toString());
    if (filters.minSeats) params.set('minSeats', filters.minSeats.toString());
    if (filters.minSeats && !filters.includeOptionalSeats) params.set('optSeats', '0');
    if (filters.minPower) params.set('minPower', filters.minPower.toString());
    if (filters.minChargingDcKw) params.set('minChargingDcKw', filters.minChargingDcKw.toString());
    if (filters.maxAcceleration) params.set('maxAcceleration', filters.maxAcceleration.toString());
    if (filters.brandId) params.set('brandId', filters.brandId);
    if (filters.modelId) params.set('modelId', filters.modelId);
    if (filters.showUpcoming) params.set('showUpcoming', filters.showUpcoming);
    if (filters.minIsofix) params.set('minIsofix', filters.minIsofix.toString());
    if (filters.threeIsofixSecondRow !== 'any') params.set('iso3row', filters.threeIsofixSecondRow);
    if (sort !== 'updated_desc') params.set('sort', sort);
    if (page > 1) params.set('page', page.toString());
    if (compareMode) params.set('compare', '1');
    setSearchParams(params, { replace: true });
  }, [filters, sort, page, compareMode, setSearchParams]);

  // Reset page when filters, sort or view mode change
  useEffect(() => {
    setPage(1);
  }, [filters, sort, viewMode]);

  // Group cars by model for model view
  const modelGroups = useMemo(() => groupCarsByModel(cars), [cars]);

  // Items shown depends on view mode
  const totalItems = viewMode === 'model' ? modelGroups.length : cars.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const clampedPage = Math.min(page, totalPages);
  const paginatedCars = useMemo(() => {
    const start = (clampedPage - 1) * ITEMS_PER_PAGE;
    return cars.slice(start, start + ITEMS_PER_PAGE);
  }, [cars, clampedPage]);
  const paginatedGroups = useMemo(() => {
    const start = (clampedPage - 1) * ITEMS_PER_PAGE;
    return modelGroups.slice(start, start + ITEMS_PER_PAGE);
  }, [modelGroups, clampedPage]);

  const handleShowVariants = (modelId: string) => {
    setFilters(prev => ({ ...prev, modelId }));
    setViewMode('variant');
  };

  const goToPage = (p: number) => {
    setPage(p);
    const el = resultsRef.current;
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (clampedPage > 3) pages.push('ellipsis');
      for (let i = Math.max(2, clampedPage - 1); i <= Math.min(totalPages - 1, clampedPage + 1); i++) {
        pages.push(i);
      }
      if (clampedPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  const handleSearch = (query: string) => {
    setFilters(prev => ({ ...prev, query }));
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
  };

  const activeFilterCount = [
    filters.query,
    filters.maxPrice,
    filters.powertrain.length > 0,
    filters.bodyType.length > 0,
    filters.drivetrain,
    filters.minRange,
    filters.minTrunk,
    filters.minTowing,
    filters.minSeats,
    filters.minPower,
    filters.minChargingDcKw,
    filters.maxAcceleration,
    filters.brandId,
    filters.modelId,
    filters.minIsofix,
    filters.threeIsofixSecondRow !== 'any',
    filters.showUpcoming && filters.showUpcoming !== 'all',
  ].filter(Boolean).length;


  return (
    <PublicLayout>
      <Seo
        title="Alle elbiler i Danmark – pris, rækkevidde og udstyr"
        description="Find og filtrér elbiler på det danske marked. Sammenlign pris, batteristørrelse, rækkevidde, ladehastighed, udstyr og praktiske egenskaber."
        path="/biler"
        noindex={activeFilterCount > 0}
        jsonLd={[
          breadcrumbJsonLd([HOME_CRUMB, { name: 'Biler', path: '/biler' }]),
          ...(paginatedCars.length > 0 ? [{
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Biler",
            numberOfItems: cars.length,
            itemListElement: paginatedCars.map((c: any, i: number) => {
              const brand = c.models?.brands;
              const model = c.models;
              return {
                "@type": "ListItem",
                position: (clampedPage - 1) * ITEMS_PER_PAGE + i + 1,
                name: `${brand?.name ?? ''} ${model?.name ?? ''} ${c.name ?? ''}`.trim(),
                url: brand?.slug && model?.slug && c.slug
                  ? `https://minnyeelbil.dk/biler/${brand.slug}/${model.slug}/${c.slug}`
                  : undefined,
              };
            }).filter((x: any) => x.url),
          }] : []),
        ]}

      />
            <div className="bg-muted/50 min-h-screen">
        {/* Header */}
        <div className="bg-primary text-primary-foreground py-5 sm:py-8">
          <div className="container mx-auto px-4">
            <h1 className="font-display text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">Find elbiler i Danmark</h1>
            <p className="text-primary-foreground/85 text-sm sm:text-base max-w-2xl mb-4">
              Find elbiler på det danske marked og sammenlign dem på rækkevidde, pris, batteristørrelse, ladehastighed, forbrug, udstyr og praktiske egenskaber. Brug filtrene til at finde den elbil, der passer bedst til dit behov.
            </p>

            
            {/* Search Bar with Autocomplete */}
            <div className="max-w-2xl">
              <CarSearchAutocomplete
                onSearch={handleSearch}
                defaultValue={filters.query}
                inputClassName="bg-background text-foreground h-12"
              />
            </div>
          </div>
        </div>

        {compareMode && (
          <div className="bg-accent/10 border-b border-accent/30">
            <div className="container mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-sm">
                <span className="font-medium">Du vælger biler til sammenligning.</span>{' '}
                <span className="text-muted-foreground">Vælg de specifikke varianter, du vil sammenligne — klik “Sammenlign” på bilkortet.</span>
              </p>
              <Button asChild size="sm" variant="outline">
                <a href="/sammenlign">Gå til sammenligning</a>
              </Button>
            </div>
          </div>
        )}

        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Desktop Filters Sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-4">
                <CarSearchFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  brands={brands}
                  models={models}
                  onReset={handleResetFilters}
                  viewMode={compareMode ? undefined : viewMode}
                  onViewModeChange={compareMode ? undefined : setViewMode}
                />
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-col gap-3 mb-6">
                <div className="flex items-center gap-2">
                  {/* Mobile Filter Button */}
                  <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="lg:hidden relative flex-1 sm:flex-initial min-h-11">
                        <SlidersHorizontal className="w-4 h-4 mr-2" />
                        Filtre
                        {activeFilterCount > 0 && (
                          <span className="absolute -top-2 -right-2 w-5 h-5 bg-accent text-accent-foreground text-xs rounded-full flex items-center justify-center">
                            {activeFilterCount}
                          </span>
                        )}
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 p-0 flex flex-col">
                      <SheetHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4 flex-row items-center justify-between space-y-0">
                        <SheetTitle>Filtre</SheetTitle>
                        <Button
                          size="sm"
                          onClick={() => setMobileFiltersOpen(false)}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground mr-6"
                        >
                          Vis {cars.length}
                        </Button>
                      </SheetHeader>
                      <div className="flex-1 overflow-y-auto px-6 py-4">
                        <CarSearchFilters
                          filters={filters}
                          onFiltersChange={(f) => {
                            setFilters(f);
                          }}
                          brands={brands}
                          models={models}
                          onReset={handleResetFilters}
                          viewMode={compareMode ? undefined : viewMode}
                          onViewModeChange={compareMode ? undefined : setViewMode}
                        />
                      </div>
                      <div className="sticky bottom-0 bg-background border-t px-6 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
                        <Button
                          onClick={() => setMobileFiltersOpen(false)}
                          disabled={cars.length === 0}
                          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold min-h-11"
                        >
                          {isLoading
                            ? 'Søger…'
                            : cars.length === 0
                              ? 'Ingen resultater'
                              : `Vis ${cars.length} elbiler`}
                        </Button>
                      </div>
                    </SheetContent>
                  </Sheet>

                  {/* Sort Dropdown */}
                  <div className="flex items-center gap-2 flex-1 sm:flex-initial sm:ml-auto">
                    <ArrowUpDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
                    <Select value={sort} onValueChange={(value) => setSort(value as SortOption)}>
                      <SelectTrigger className="w-full sm:w-[200px] min-h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sortOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <SaveSearchButton
                    defaultName={`Elbiler${activeFilterCount > 0 ? ' med mine filtre' : ''}`}
                  />


                  <ShareButton
                    context="search"
                    title={`${cars.length} elbiler matcher ${activeFilterCount > 0 ? 'mine filtre' : 'søgningen'}`}
                    iconOnly
                    className="min-h-11"
                  />
                </div>

                <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-0.5 sm:flex-wrap sm:overflow-visible">
                  <span className="hidden sm:inline text-sm text-muted-foreground mr-1">Vis:</span>
                  {([
                    { value: 'all', label: 'Alle' },
                    { value: 'exclude', label: 'På markedet' },
                    { value: 'only', label: 'Kommende' },
                  ] as const).map((opt) => {
                    const current = filters.showUpcoming || 'all';
                    const active = current === opt.value;
                    return (
                      <Button
                        key={opt.value}
                        size="sm"
                        variant={active ? 'default' : 'outline'}
                        onClick={() => setFilters({ ...filters, showUpcoming: opt.value })}
                        className="min-h-9 flex-shrink-0"
                      >
                        {opt.label}
                      </Button>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm text-muted-foreground">
                    {isLoading
                      ? 'Søger...'
                      : viewMode === 'model'
                        ? `${modelGroups.length} ${modelGroups.length === 1 ? 'model' : 'modeller'} fundet`
                        : `${cars.length} ${cars.length === 1 ? 'variant' : 'varianter'} fundet`}
                  </p>
                  {!compareMode && <ViewModeToggle value={viewMode} onChange={setViewMode} />}
                </div>

              </div>

              {/* Results Grid */}
              <div ref={resultsRef}>
              {isError ? (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2 text-destructive">Der opstod en fejl</h2>
                  <p className="text-muted-foreground mb-4">
                    {error instanceof Error ? error.message : 'Kunne ikke hente elbiler fra databasen'}
                  </p>
                  <Button onClick={() => refetch()} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Prøv igen
                  </Button>
                </div>
              ) : isLoading ? (
                <CarCardGridSkeleton count={6} />
              ) : cars.length > 0 ? (
                <>
                  {filters.showUpcoming !== 'exclude' && (() => {
                    const upcomingCount = cars.filter(c => c.variant.availability_status === 'upcoming').length;
                    if (upcomingCount === 0) return null;
                    return (
                      <div className="mb-4 text-sm text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2">
                        {upcomingCount} af resultaterne er <span className="font-medium text-foreground">kommende elbiler</span> – endnu ikke til salg.
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {viewMode === 'model'
                      ? paginatedGroups.map((g) => (
                          <ModelCard key={g.modelId} group={g} onShowVariants={handleShowVariants} />
                        ))
                      : paginatedCars.map((car) => (
                          <CarCard key={car.variant.id} car={car} />
                        ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex flex-col items-center gap-3">
                      <p className="text-sm text-muted-foreground">
                        Side {clampedPage} af {totalPages} — {totalItems} {viewMode === 'model' ? 'modeller' : 'varianter'}
                      </p>
                      <Pagination>
                        <PaginationContent>
                          {clampedPage > 1 && (
                            <PaginationItem>
                              <PaginationPrevious
                                onClick={() => goToPage(clampedPage - 1)}
                                className="cursor-pointer"
                              >
                                Forrige
                              </PaginationPrevious>
                            </PaginationItem>
                          )}
                          {getPageNumbers().map((p, i) =>
                            p === 'ellipsis' ? (
                              <PaginationItem key={`ellipsis-${i}`}>
                                <PaginationEllipsis />
                              </PaginationItem>
                            ) : (
                              <PaginationItem key={p}>
                                <PaginationLink
                                  isActive={p === clampedPage}
                                  onClick={() => goToPage(p)}
                                  className="cursor-pointer"
                                >
                                  {p}
                                </PaginationLink>
                              </PaginationItem>
                            )
                          )}
                          {clampedPage < totalPages && (
                            <PaginationItem>
                              <PaginationNext
                                onClick={() => goToPage(clampedPage + 1)}
                                className="cursor-pointer"
                              >
                                Næste
                              </PaginationNext>
                            </PaginationItem>
                          )}
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-card rounded-xl shadow-card p-12 text-center">
                  <Car className="w-16 h-16 text-muted-foreground/60 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Ingen elbiler fundet</h2>
                  <p className="text-muted-foreground mb-4">
                    {filters.query || activeFilterCount > 0
                      ? 'Prøv at ændre dine filtre eller søgeord'
                      : 'Der er endnu ikke tilføjet nogen elbiler til databasen'}
                  </p>
                  {activeFilterCount > 0 && (
                    <Button variant="outline" onClick={handleResetFilters}>
                      Nulstil filtre
                    </Button>
                  )}
                </div>
              )}
              </div>
            </main>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
